import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PromptTextHighlight } from '../../components/PromptTextHighlight';
import { usePromptHighlightOverlayBindings } from '../../hooks/usePromptHighlightOverlayBindings';
import { promptHighlightOverlayHandlers } from '../../utils/promptHighlightOverlay';
import type { MatNode, MediaType, PromptItem, PromptMentionCandidate } from '../../store/seedanceTypes';
import { DEFAULT_SEEDANCE_MODEL } from '../../store/seedanceWorkspaceStore';
import {
  WORKSPACE_MAX_ACTIVE_AUDIOS,
  WORKSPACE_MAX_ACTIVE_IMAGES,
  WORKSPACE_MAX_ACTIVE_VIDEOS,
  WORKSPACE_MAX_PROMPTS,
} from '../../utils/seedanceConstants';
import { useSeedanceLibraryStore } from '../../store/seedanceLibraryStore';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import {
  adjustPromptItemsAfterSlotInserted,
  adjustPromptItemsAfterSlotRemoved,
  buildPromptMentionLabel,
  findPromptMentionCandidate,
  getConnectedMentionIndexForNode,
  getMentionIndexOnConnect,
  getPromptMentionAtIndex,
  getTextareaIndexNearPoint,
  promptTextHasMediaRefSegments,
  stripPromptMentionsForSubmit,
} from '../../utils/seedancePromptUtils';
import {
  cloneRegenerateDraft,
  type VideoRegenerateDraft,
} from '../../utils/taskRegenerateDraft';
import { refreshMatNodesPortraitUrls } from '../../utils/seedanceWorkspaceMatRefresh';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
} from '../../utils/seedanceStorage';
import { MaterialNodeCard } from '../workspace/components/MaterialNodeCard/MaterialNodeCard';
import '../workspace/workspace.css';
import '../../components/ConfirmModal/confirmModal.css';
import './taskDetail.css';
import './taskRegenerateModal.css';

type RegenFormState = Omit<VideoRegenerateDraft, 'prompts'> & {
  promptItems: PromptItem[];
};

function draftToForm(d: VideoRegenerateDraft): RegenFormState {
  const clone = cloneRegenerateDraft(d);
  const { prompts, ...rest } = clone;
  return {
    ...rest,
    /** 与创作工作台相同：提交 payload 始终带标准版 ID，网关按 resolution 改写 model。 */
    model: DEFAULT_SEEDANCE_MODEL,
    promptItems: prompts.map((text, i) => ({ id: i, text })),
  };
}

function formToDraft(f: RegenFormState): VideoRegenerateDraft {
  return {
    ...f,
    model: DEFAULT_SEEDANCE_MODEL,
    prompts: f.promptItems.map((p) => p.text),
  };
}

function nextNodeSeq(nodes: MatNode[]): number {
  let max = 0;
  for (const n of nodes) {
    const m = /^regen_mn_(\d+)$/.exec(n.id);
    if (m) max = Math.max(max, parseInt(m[1], 10));
    const m2 = /^regen_mat_(\d+)$/.exec(n.id);
    if (m2) max = Math.max(max, parseInt(m2[1], 10));
  }
  return max + 1;
}

export type TaskRegenerateModalProps = {
  open: boolean;
  initialDraft: VideoRegenerateDraft | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (draft: VideoRegenerateDraft) => void;
  onLimitMessage?: (message: string) => void;
};

const MentionThumbPopover: React.FC<{
  hover: { thumbUrl: string | null; label: string; x: number; y: number } | null;
}> = ({ hover }) => {
  if (!hover) return null;
  const popW = 180;
  const popH = 210;
  const pad = 14;
  let left = hover.x + pad;
  let top = hover.y + pad;
  if (typeof window !== 'undefined') {
    left = Math.min(left, window.innerWidth - popW - 10);
    top = Math.min(top, window.innerHeight - popH - 10);
    left = Math.max(10, left);
    top = Math.max(10, top);
  }
  return (
    <div className="prompt-mention-thumb-pop" style={{ left, top }} role="tooltip" aria-hidden>
      {hover.thumbUrl ? (
        <img
          src={hover.thumbUrl}
          alt=""
          className="prompt-mention-thumb-pop-img"
          draggable={false}
        />
      ) : (
        <div className="prompt-mention-thumb-pop-empty">暂无预览图</div>
      )}
      <div className="prompt-mention-thumb-pop-cap">{hover.label}</div>
    </div>
  );
};

export const TaskRegenerateModal: React.FC<TaskRegenerateModalProps> = ({
  open,
  initialDraft,
  submitting,
  onClose,
  onSubmit,
  onLimitMessage,
}) => {
  const titleId = React.useId();
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const clearLibPicker = useSeedanceLibraryStore((s) => s.clearLibPicker);
  const setLibPickerExternalConsumer = useSeedanceLibraryStore((s) => s.setLibPickerExternalConsumer);
  const setLibPickerTarget = useSeedanceLibraryStore((s) => s.setLibPickerTarget);
  const [form, setForm] = useState<RegenFormState | null>(null);

  const promptSeqRef = useRef(1);
  const regenNodeSeqRef = useRef(1);
  const promptInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const promptHighlightRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const promptCursorRef = useRef<Record<number, number>>({});
  const dragNodeIdRef = useRef<string | null>(null);
  const [mentionState, setMentionState] = useState<{ promptId: number | null; query: string }>({
    promptId: null,
    query: '',
  });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [matPreview, setMatPreview] = useState<{ url: string; type: 'image' | 'video' } | null>(
    null,
  );
  const [mentionThumbHover, setMentionThumbHover] = useState<{
    thumbUrl: string | null;
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const mentionThumbHoverRaf = useRef<number | null>(null);
  const regenPortraitAbortRef = useRef<AbortController | null>(null);
  const regenPortraitRetryTsRef = useRef<Record<string, number>>({});

  usePromptHighlightOverlayBindings(
    (form?.promptItems ?? []).map((p) => p.id),
    (form?.promptItems ?? []).map((p) => p.text),
    promptInputRefs,
    promptHighlightRefs,
  );

  const hydrateFromDraft = useCallback((d: VideoRegenerateDraft) => {
    const cloned = cloneRegenerateDraft(d);
    const nextForm = draftToForm(cloned);
    const nPrompts = nextForm.promptItems.length;
    promptSeqRef.current = Math.max(
      nPrompts ? Math.max(...nextForm.promptItems.map((p) => p.id)) + 1 : 1,
      1,
    );
    regenNodeSeqRef.current = nextNodeSeq(nextForm.nodes);
    setForm(nextForm);
  }, []);

  useEffect(() => {
    if (open && initialDraft) hydrateFromDraft(initialDraft);
    if (!open) {
      setForm(null);
      setMentionState({ promptId: null, query: '' });
      setMentionThumbHover(null);
      setMatPreview(null);
      if (useSeedanceLibraryStore.getState().libPickerExternalConsumer) {
        useSeedanceLibraryStore.getState().clearLibPicker();
      }
    }
  }, [open, initialDraft, hydrateFromDraft]);

  const applyRegenPortraitPatch = useCallback(
    (patch: Map<string, { sourceAssetId: string; url: string; thumb: string }>) => {
      if (patch.size === 0) return;
      setForm((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          nodes: prev.nodes.map((node) => {
            const u = patch.get(node.id);
            if (!u) return node;
            if (u.sourceAssetId !== (node.sourceAssetId || '').trim()) return node;
            return {
              ...node,
              ...(u.url ? { url: u.url } : {}),
              ...(u.thumb ? { thumb: u.thumb } : {}),
            };
          }),
        };
      });
    },
    [],
  );

  const flushRegenPortraitUrls = useCallback(
    (nodesOverride?: MatNode[]) => {
      const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
      const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
      if (!token || !userId) return;

      const cur = nodesOverride ?? form?.nodes ?? [];
      const targets = cur.filter((n) => {
        const sid = (n.sourceAssetId || '').trim();
        if (!sid || (n.type !== 'image' && n.type !== 'video' && n.type !== 'audio')) return false;
        if (nodesOverride) return true;
        return !(n.url || '').trim();
      });
      if (!targets.length) return;

      regenPortraitAbortRef.current?.abort();
      const ac = new AbortController();
      regenPortraitAbortRef.current = ac;

      void (async () => {
        try {
          const patch = await refreshMatNodesPortraitUrls(targets, {
            onDebug: addDebug,
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          applyRegenPortraitPatch(patch);
        } catch {
          /* 网络失败时保留占位 */
        }
      })();
    },
    [addDebug, applyRegenPortraitPatch, form?.nodes],
  );

  useEffect(() => {
    if (!open || !form) return;
    const t = window.setTimeout(() => flushRegenPortraitUrls(), 300);
    return () => window.clearTimeout(t);
  }, [open, form, flushRegenPortraitUrls]);

  useEffect(() => {
    return () => regenPortraitAbortRef.current?.abort();
  }, []);

  const onRegenMatPreviewImageError = useCallback(
    (nodeId: string) => {
      const n = form?.nodes.find((x) => x.id === nodeId);
      if (!n || n.type !== 'image') return;
      const sid = (n.sourceAssetId || '').trim();
      if (!sid) return;

      const now = Date.now();
      const last = regenPortraitRetryTsRef.current[nodeId] ?? 0;
      if (now - last < 45_000) return;
      regenPortraitRetryTsRef.current[nodeId] = now;
      flushRegenPortraitUrls([n]);
    },
    [flushRegenPortraitUrls, form?.nodes],
  );

  const resetToInitial = useCallback(() => {
    if (!initialDraft) return;
    hydrateFromDraft(initialDraft);
    showToast('已恢复为本次打开时的内容', 's');
  }, [initialDraft, hydrateFromDraft, showToast]);

  const updatePromptText = useCallback((id: number, text: string) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            promptItems: prev.promptItems.map((p) => (p.id === id ? { ...p, text } : p)),
          }
        : prev,
    );
  }, []);

  const mentionCandidates = useMemo<PromptMentionCandidate[]>(() => {
    const nodes = form?.nodes ?? [];
    const active = nodes.filter(
      (n) =>
        (n.type === 'image' || n.type === 'video' || n.type === 'audio') && n.connected,
    );
    let imageIdx = 0;
    let videoIdx = 0;
    let audioIdx = 0;
    return active.map((n) => {
      const label =
        n.type === 'video'
          ? buildPromptMentionLabel('video', ++videoIdx)
          : n.type === 'audio'
            ? buildPromptMentionLabel('audio', ++audioIdx)
            : buildPromptMentionLabel('image', ++imageIdx);
      return {
        nodeId: n.id,
        name: n.name,
        label,
        mention: `@${label}`,
      };
    });
  }, [form?.nodes]);

  const filteredMentionCandidates = useMemo(() => {
    if (mentionState.promptId === null) return [];
    const q = mentionState.query.trim();
    if (!q) return mentionCandidates;
    return mentionCandidates.filter(
      (c) =>
        c.label.toLowerCase().includes(q.toLowerCase()) ||
        c.name.toLowerCase().includes(q.toLowerCase()),
    );
  }, [mentionCandidates, mentionState]);

  const handlePromptChangeWithMention = useCallback(
    (id: number, text: string, cursorPos: number) => {
      promptCursorRef.current[id] = cursorPos;
      updatePromptText(id, text);
      const before = text.slice(0, cursorPos);
      const m = /@([^\s@]*)$/.exec(before);
      if (!m || mentionCandidates.length === 0) {
        if (mentionState.promptId === id) setMentionState({ promptId: null, query: '' });
        return;
      }
      setMentionState({ promptId: id, query: m[1] ?? '' });
    },
    [mentionCandidates.length, mentionState.promptId, updatePromptText],
  );

  const applyPromptMention = useCallback((promptId: number, candidate: PromptMentionCandidate) => {
    const input = promptInputRefs.current[promptId];
    const fallbackCursor = input?.selectionStart ?? 0;
    const cursor = promptCursorRef.current[promptId] ?? fallbackCursor;
    setForm((prev) => {
      if (!prev) return prev;
      const nextItems = prev.promptItems.map((p) => {
        if (p.id !== promptId) return p;
        const before = p.text.slice(0, cursor);
        const after = p.text.slice(cursor);
        const replacedBefore = before.replace(/@([^\s@]*)$/, `${candidate.mention} `);
        const nextText = replacedBefore + after;
        const nextCursor = replacedBefore.length;
        promptCursorRef.current[promptId] = nextCursor;
        window.setTimeout(() => {
          const el = promptInputRefs.current[promptId];
          if (!el) return;
          el.focus();
          el.setSelectionRange(nextCursor, nextCursor);
        }, 0);
        return { ...p, text: nextText };
      });
      return { ...prev, promptItems: nextItems };
    });
    setMentionState({ promptId: null, query: '' });
  }, []);

  const clearPrompt = useCallback((id: number) => updatePromptText(id, ''), [updatePromptText]);

  const flushMentionThumbHover = useCallback(
    (ta: HTMLTextAreaElement, clientX: number, clientY: number) => {
      const text = ta.value;
      if (!promptTextHasMediaRefSegments(text)) {
        setMentionThumbHover(null);
        return;
      }
      const idx = text.length > 8000 ? -1 : getTextareaIndexNearPoint(ta, clientX, clientY);
      const ref = idx >= 0 ? getPromptMentionAtIndex(text, idx) : null;
      if (ref == null) {
        setMentionThumbHover(null);
        return;
      }
      const cand = findPromptMentionCandidate(mentionCandidates, ref);
      const label = cand ? cand.mention : `@${buildPromptMentionLabel(ref.kind, ref.num)}`;
      if (!cand) {
        setMentionThumbHover({ thumbUrl: null, label, x: clientX, y: clientY });
        return;
      }
      const nodes = form?.nodes ?? [];
      const node = nodes.find((n) => n.id === cand.nodeId);
      const thumbUrl = (node?.thumb || node?.url || '').trim() || null;
      setMentionThumbHover({ thumbUrl, label, x: clientX, y: clientY });
    },
    [mentionCandidates, form?.nodes],
  );

  const onPromptMouseMoveMentionThumb = useCallback(
    (e: React.MouseEvent<HTMLTextAreaElement>) => {
      const ta = e.currentTarget;
      const cx = e.clientX;
      const cy = e.clientY;
      if (mentionThumbHoverRaf.current != null) cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = requestAnimationFrame(() => {
        mentionThumbHoverRaf.current = null;
        if (!ta?.isConnected) return;
        flushMentionThumbHover(ta, cx, cy);
      });
    },
    [flushMentionThumbHover],
  );

  const onPromptMouseLeaveMentionThumb = useCallback(() => {
    if (mentionThumbHoverRaf.current != null) {
      cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = null;
    }
    setMentionThumbHover(null);
  }, []);

  const addPromptRow = useCallback(() => {
    setForm((prev) => {
      if (!prev) return prev;
      const id = promptSeqRef.current++;
      return { ...prev, promptItems: [...prev.promptItems, { id, text: '' }] };
    });
  }, []);

  const removePromptRow = useCallback((id: number) => {
    setForm((prev) => {
      if (!prev || prev.promptItems.length <= 1) return prev;
      return { ...prev, promptItems: prev.promptItems.filter((p) => p.id !== id) };
    });
  }, []);

  const addMatSlot = useCallback((type: MediaType) => {
    setForm((prev) => {
      if (!prev) return prev;
      const id = `regen_mn_${regenNodeSeqRef.current++}`;
      const countSame = prev.nodes.filter((n) => n.type === type).length;
      const name =
        type === 'image'
          ? `图片${countSame + 1}`
          : type === 'video'
            ? `视频${countSame + 1}`
            : `音频${countSame + 1}`;
      const node: MatNode = {
        id,
        type,
        name,
        url: '',
        thumb: '',
        connected: false,
      };
      return { ...prev, nodes: [...prev.nodes, node] };
    });
  }, []);

  const toggleNodeConnected = useCallback(
    (nodeId: string) => {
      const currentNodes = form?.nodes;
      if (!currentNodes) return;
      const target = currentNodes.find((n) => n.id === nodeId);
      if (!target) return;

      if (target.connected) {
        const ref = getConnectedMentionIndexForNode(currentNodes, nodeId);
        setForm((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            nodes: prev.nodes.map((n) =>
              n.id === nodeId ? { ...n, connected: false } : n,
            ),
            promptItems: ref
              ? adjustPromptItemsAfterSlotRemoved(prev.promptItems, ref.kind, ref.num)
              : prev.promptItems,
          };
        });
        return;
      }

      setForm((prev) => {
        if (!prev) return prev;
        const t = prev.nodes.find((n) => n.id === nodeId);
        if (!t) return prev;
        const activeImgs = prev.nodes.filter((n) => n.type === 'image' && n.connected).length;
        const activeVids = prev.nodes.filter((n) => n.type === 'video' && n.connected).length;
        const activeAuds = prev.nodes.filter((n) => n.type === 'audio' && n.connected).length;
        if (t.type === 'image' && activeImgs >= WORKSPACE_MAX_ACTIVE_IMAGES) {
          onLimitMessage?.(`最多激活 ${WORKSPACE_MAX_ACTIVE_IMAGES} 张图片`);
          return prev;
        }
        if (t.type === 'video' && activeVids >= WORKSPACE_MAX_ACTIVE_VIDEOS) {
          onLimitMessage?.(`最多激活 ${WORKSPACE_MAX_ACTIVE_VIDEOS} 个视频`);
          return prev;
        }
        if (t.type === 'audio' && activeAuds >= WORKSPACE_MAX_ACTIVE_AUDIOS) {
          onLimitMessage?.(`最多激活 ${WORKSPACE_MAX_ACTIVE_AUDIOS} 个音频`);
          return prev;
        }
        const insertRef = getMentionIndexOnConnect(prev.nodes, nodeId);
        return {
          ...prev,
          nodes: prev.nodes.map((n) =>
            n.id === nodeId ? { ...n, connected: true } : n,
          ),
          promptItems: insertRef
            ? adjustPromptItemsAfterSlotInserted(prev.promptItems, insertRef.kind, insertRef.num)
            : prev.promptItems,
        };
      });
    },
    [form?.nodes, onLimitMessage],
  );

  const removeNode = useCallback(
    (id: string) => {
      const currentNodes = form?.nodes;
      if (!currentNodes) return;
      const ref = getConnectedMentionIndexForNode(currentNodes, id);
      setForm((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          nodes: prev.nodes.filter((x) => x.id !== id),
          promptItems: ref
            ? adjustPromptItemsAfterSlotRemoved(prev.promptItems, ref.kind, ref.num)
            : prev.promptItems,
        };
      });
    },
    [form?.nodes],
  );

  const updateNodeName = useCallback((id: string, val: string) => {
    setForm((prev) =>
      prev ? { ...prev, nodes: prev.nodes.map((n) => (n.id === id ? { ...n, name: val } : n)) } : prev,
    );
  }, []);

  const finalizeNodeName = useCallback((id: string) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            nodes: prev.nodes.map((n) =>
              n.id === id ? { ...n, name: n.name.trim() || '未命名' } : n,
            ),
          }
        : prev,
    );
  }, []);

  const onNodeDragStart = (id: string) => (e: React.DragEvent) => {
    dragNodeIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onNodeDragEnd = () => {
    setDraggingId(null);
    dragNodeIdRef.current = null;
    setDragOverId(null);
  };
  const onNodeDragOver = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragNodeIdRef.current && dragNodeIdRef.current !== id) setDragOverId(id);
  };
  const onNodeDragLeave = () => setDragOverId(null);
  const onNodeDrop = (targetId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromId = dragNodeIdRef.current;
    if (!fromId || fromId === targetId) return;
    setForm((prev) => {
      if (!prev) return prev;
      const arr = prev.nodes.filter(
        (n) => n.type === 'image' || n.type === 'video' || n.type === 'audio',
      );
      const fromIdx = arr.findIndex((n) => n.id === fromId);
      const toIdx = arr.findIndex((n) => n.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = arr.slice();
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      const rest = prev.nodes.filter(
        (n) => n.type !== 'image' && n.type !== 'video' && n.type !== 'audio',
      );
      return { ...prev, nodes: [...next, ...rest] };
    });
    setDragOverId(null);
  };

  const onOpenLibPicker = useCallback(
    (nodeId: string) => {
      if (submitting) return;
      const node = form?.nodes.find((n) => n.id === nodeId);
      if (!node || (node.type !== 'image' && node.type !== 'video' && node.type !== 'audio')) {
        return;
      }
      clearLibPicker();
      setLibPickerExternalConsumer({
        nodeId,
        mediaType: node.type,
        onApply: (asset) => {
          const sid =
            (asset.sourceAssetId && String(asset.sourceAssetId).trim()) ||
            (asset.id !== undefined && String(asset.id).trim()) ||
            undefined;
          const nextNode: MatNode = {
            ...(form?.nodes.find((n) => n.id === nodeId) ?? node),
            url: asset.url || '',
            thumb: asset.thumb || asset.url || '',
            name: asset.name,
            fileSize: asset.fileSize,
            duration: asset.duration,
            ...(sid ? { sourceAssetId: sid } : {}),
          };
          setForm((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              nodes: prev.nodes.map((n) => (n.id === nodeId ? nextNode : n)),
            };
          });
          if (sid && !(asset.url || '').trim()) {
            queueMicrotask(() => flushRegenPortraitUrls([nextNode]));
          }
          showToast(`已应用素材: ${asset.name}`, 's');
        },
      });
      setLibPickerTarget(nodeId);
    },
    [
      form?.nodes,
      submitting,
      showToast,
      clearLibPicker,
      flushRegenPortraitUrls,
      setLibPickerExternalConsumer,
      setLibPickerTarget,
    ],
  );

  const counts = useMemo(() => {
    const nodes = form?.nodes ?? [];
    const activeImgs = nodes.filter((n) => n.type === 'image' && n.connected).length;
    const activeVids = nodes.filter((n) => n.type === 'video' && n.connected).length;
    const activeAuds = nodes.filter((n) => n.type === 'audio' && n.connected).length;
    const prompts = form?.promptItems.length ?? 0;
    return { activeImgs, activeVids, activeAuds, prompts };
  }, [form?.nodes, form?.promptItems.length]);

  const handleSubmit = () => {
    if (!form) return;
    const draft = formToDraft(form);
    const nonempty = draft.prompts
      .map((p) => stripPromptMentionsForSubmit(p.trim()))
      .filter(Boolean);
    if (!nonempty.length) {
      onLimitMessage?.('请至少输入一个提示词');
      return;
    }
    onSubmit(draft);
  };

  if (!open) return null;

  const promptItems = form?.promptItems ?? [];

  return (
    <div className="confirm-modal-mask">
      <div
        className="confirm-modal-box task-regen-modal-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="confirm-modal-close"
          onClick={onClose}
          aria-label="关闭"
          disabled={submitting}
        >
          ×
        </button>
        <h3 id={titleId}>重新生成</h3>
        <div className="confirm-modal-body task-regen-modal-body">
          {!form ? (
            <p className="task-detail-empty">加载配置中…</p>
          ) : (
            <>
              <div className="task-regen-ws-shell">
                <div className="float-toolbar task-regen-top-toolbar">
                  <button
                    type="button"
                    className="ft-btn add-img"
                    onClick={() => addMatSlot('image')}
                    disabled={submitting}
                  >
                    🖼️ 新增图片
                  </button>
                  <button
                    type="button"
                    className="ft-btn add-vid"
                    onClick={() => addPromptRow()}
                    disabled={submitting}
                  >
                    📝 新增提示词
                  </button>
                  <button
                    type="button"
                    className="ft-btn add-mat-video"
                    onClick={() => addMatSlot('video')}
                    disabled={submitting}
                  >
                    🎥 新增视频
                  </button>
                  <button
                    type="button"
                    className="ft-btn add-audio"
                    onClick={() => addMatSlot('audio')}
                    disabled={submitting}
                  >
                    🎵 新增音频
                  </button>
                </div>

                <div className="workspace">
                  <div className="workspace-toolbar-row">
                    <div className="count-bar" style={{ marginBottom: 0, width: 'auto' }}>
                      <div className="cb-item" style={{ color: 'var(--orange)' }}>
                        <div className="cb-dot" />
                        图片 <b>{counts.activeImgs}/{WORKSPACE_MAX_ACTIVE_IMAGES}</b>
                      </div>
                      <div className="cb-item" style={{ color: 'var(--pink)' }}>
                        <div className="cb-dot" />
                        提示词 <b>{counts.prompts}/{WORKSPACE_MAX_PROMPTS}</b>
                      </div>
                      <div className="cb-item" style={{ color: 'var(--green)' }}>
                        <div className="cb-dot" />
                        视频 <b>{counts.activeVids}/{WORKSPACE_MAX_ACTIVE_VIDEOS}</b>
                      </div>
                      <div className="cb-item" style={{ color: 'var(--blue)' }}>
                        <div className="cb-dot" />
                        音频 <b>{counts.activeAuds}/{WORKSPACE_MAX_ACTIVE_AUDIOS}</b>
                      </div>
                    </div>
                    <button type="button" className="btn" onClick={resetToInitial} disabled={submitting}>
                      🗑️ 重置
                    </button>
                  </div>

                  <div>
                    {promptItems.map((p, idx) => (
                      <div className="node prompt-node" key={p.id}>
                        <div className="node-head">
                          <div className="node-ico">🎯</div>
                          <span className="node-title">
                            {idx === 0 ? '提示词' : `提示词 ${idx + 1}`}
                          </span>
                          {idx === 0 ? (
                            <button
                              type="button"
                              onClick={() => addPromptRow()}
                              disabled={submitting}
                              style={{
                                background: 'var(--purple-dim)',
                                border: '1px solid var(--purple)',
                                borderRadius: 6,
                                padding: '4px 10px',
                                color: 'var(--text-primary)',
                                fontSize: 11,
                                cursor: 'pointer',
                              }}
                            >
                              ➕ 添加
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removePromptRow(p.id)}
                              disabled={submitting}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                cursor: 'pointer',
                                fontSize: 16,
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div className="node-body">
                          <div className="prompt-ta-wrap">
                            <div
                              className="prompt-ta-highlight"
                              inert
                              ref={(el) => {
                                promptHighlightRefs.current[p.id] = el;
                              }}
                            >
                              <PromptTextHighlight text={p.text} />
                            </div>
                            <textarea
                              className="prompt-ta prompt-ta--overlay-input"
                              value={p.text}
                              ref={(el) => {
                                promptInputRefs.current[p.id] = el;
                              }}
                              onChange={(e) =>
                                handlePromptChangeWithMention(
                                  p.id,
                                  e.target.value,
                                  e.currentTarget.selectionStart ?? e.target.value.length,
                                )
                              }
                              {...promptHighlightOverlayHandlers(
                                () => promptHighlightRefs.current[p.id],
                              )}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setMentionState((s) =>
                                    s.promptId === p.id ? { promptId: null, query: '' } : s,
                                  );
                                }, 120);
                              }}
                              onMouseMove={onPromptMouseMoveMentionThumb}
                              onMouseLeave={onPromptMouseLeaveMentionThumb}
                              placeholder="描述视频内容…"
                              rows={4}
                              disabled={submitting}
                            />
                          </div>
                          {mentionState.promptId === p.id && filteredMentionCandidates.length > 0 ? (
                            <div
                              style={{
                                marginTop: 6,
                                border: '1px solid var(--border)',
                                borderRadius: 8,
                                background: 'var(--surface-2)',
                                overflow: 'hidden',
                              }}
                            >
                              {filteredMentionCandidates.map((c) => (
                                <button
                                  key={c.nodeId}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => applyPromptMention(p.id, c)}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '8px 10px',
                                    border: 'none',
                                    borderBottom: '1px solid var(--border)',
                                    background: 'transparent',
                                    color: 'var(--text-primary)',
                                    fontSize: 12,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <span>{c.mention}</span>
                                  <span style={{ color: 'var(--text-muted)' }}>{c.name}</span>
                                </button>
                              ))}
                            </div>
                          ) : null}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div className="prompt-footer" style={{ margin: 0 }}>
                              <span>
                                <code>@</code> 提及已激活素材（提交时自动替换为图N/视频N/音频N）
                              </span>
                              <span>{p.text.length} 字</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => clearPrompt(p.id)}
                              disabled={submitting}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                cursor: 'pointer',
                                fontSize: 12,
                                padding: '2px 6px',
                                borderRadius: 4,
                              }}
                              title="清空"
                            >
                              🗑️ 清空
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mat-grid">
                    {form.nodes
                      .filter((n) => n.type === 'image' || n.type === 'video' || n.type === 'audio')
                      .map((n) => (
                        <MaterialNodeCard
                          key={n.id}
                          node={n}
                          draggingId={draggingId}
                          dragOverId={dragOverId}
                          onDragStart={onNodeDragStart}
                          onDragEnd={onNodeDragEnd}
                          onDragOver={onNodeDragOver}
                          onDragLeave={onNodeDragLeave}
                          onDrop={onNodeDrop}
                          onRemove={removeNode}
                          onUpdateName={updateNodeName}
                          onFinalizeName={finalizeNodeName}
                          onToggleConnect={toggleNodeConnected}
                          onOpenLibPicker={onOpenLibPicker}
                          onPreview={setMatPreview}
                          onPreviewImageError={onRegenMatPreviewImageError}
                        />
                      ))}
                  </div>
                </div>
              </div>

              <div className="task-regen-field">
                <label>生成参数</label>
                <div className="task-regen-settings">
                  <div>
                    <label style={{ marginBottom: 4, display: 'block' }}>大模型</label>
                    <select
                      className="p-select"
                      value={DEFAULT_SEEDANCE_MODEL}
                      onChange={() => {}}
                      disabled={submitting}
                      style={{ marginBottom: 0 }}
                      aria-readonly
                    >
                      <option value={DEFAULT_SEEDANCE_MODEL}>Seedance 2.0 标准版</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ marginBottom: 4, display: 'block' }}>
                      时长（{form.duration} 秒）
                    </label>
                    <input
                      className="p-slider"
                      type="range"
                      min={5}
                      max={15}
                      value={form.duration}
                      onChange={(e) =>
                        setForm((p) =>
                          p ? { ...p, duration: parseInt(e.target.value, 10) } : p,
                        )
                      }
                      disabled={submitting}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ marginBottom: 4, display: 'block' }}>画面比例</label>
                    <select
                      className="p-select"
                      value={form.ratio}
                      onChange={(e) =>
                        setForm((p) => (p ? { ...p, ratio: e.target.value } : p))
                      }
                      disabled={submitting}
                    >
                      <option value="16:9">16:9（默认）</option>
                      <option value="4:3">4:3</option>
                      <option value="1:1">1:1</option>
                      <option value="3:4">3:4</option>
                      <option value="9:16">9:16</option>
                      <option value="21:9">21:9</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ marginBottom: 4, display: 'block' }}>分辨率</label>
                    <select
                      className="p-select"
                      value={form.resolution}
                      onChange={(e) =>
                        setForm((p) => (p ? { ...p, resolution: e.target.value } : p))
                      }
                      disabled={submitting}
                    >
                      <option value="720p">720p 推荐</option>
                      <option value="480p">480p 快速</option>
                      <option value="1080p">1080p 高清</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            取消
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={handleSubmit}
            disabled={submitting || !form}
          >
            {submitting ? '提交中…' : '🚀 开始生成'}
          </button>
        </div>
      </div>
      <MentionThumbPopover hover={mentionThumbHover} />
      {matPreview ? (
        <div
          className="ws-mat-lightbox"
          role="presentation"
          onClick={() => setMatPreview(null)}
        >
          <button
            type="button"
            className="ws-mat-lightbox-close"
            aria-label="关闭"
            onClick={() => setMatPreview(null)}
          >
            ✕
          </button>
          <div
            className="ws-mat-lightbox-inner"
            role="dialog"
            aria-modal="true"
            aria-label="素材预览"
            onClick={(e) => e.stopPropagation()}
          >
            {matPreview.type === 'image' ? (
              <img src={matPreview.url} alt="" referrerPolicy="no-referrer" />
            ) : (
              <video
                src={matPreview.url}
                controls
                autoPlay
                muted
                playsInline
                style={{ display: 'block' }}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TaskRegenerateModal;
